import { useState } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/d72nufkmc4tbhfnc6il0';

const FinalCTA: React.FC = () => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError(false);
    const form = e.currentTarget;
    const data = new URLSearchParams();
    const els  = form.elements as HTMLFormControlsCollection;
    for (let i = 0; i < els.length; i++) {
      const el = els[i] as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
      if (el.name && el.value) data.append(el.name, el.value);
    }
    try {
      await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="estimate" className="relative overflow-hidden">
      {/* Cinematic background */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: "url('https://public.readdy.ai/ai/img_res/8379bf7c2b2e90e93c2d30c86e340e70.jpg')",
        }}
      />
      {/* Layered overlays */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#0D0C0B]/97 via-[#0D0C0B]/82 to-[#0D0C0B]/50" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0D0C0B]/40 to-transparent" />
      <div className="gold-separator absolute top-0 left-0 right-0" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-28 md:py-36">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-start">

          {/* Left — copy */}
          <div className="reveal-left">
            <div className="flex items-center gap-3 mb-8">
              <span className="gold-bar" />
              <span className="text-[#C4A84F] text-[11px] tracking-[0.4em] uppercase font-sans font-medium">
                Let's Build Something Exceptional
              </span>
            </div>

            <h2 className="font-serif text-[3.5rem] md:text-[4.5rem] lg:text-[5.5rem] text-[#F5F1E8] leading-[0.92] mb-8">
              Transform Your<br />Property Today
            </h2>

            <p className="text-[#F5F1E8]/85 text-base leading-relaxed font-sans mb-12 max-w-md">
              Masters Fencing delivers premium craftsmanship across St. Mary's and Camden County. Free estimates, honest pricing, and results that define your property for decades.
            </p>

            {/* Contact items */}
            <div className="space-y-5 mb-12">
              {[
                { icon: 'fa-phone',        text: '912-409-2031',                               href: 'tel:912-409-2031' },
                { icon: 'fa-location-dot', text: "104 Admiral's Walk Dr, St. Marys, GA 31558", href: 'https://maps.google.com/?q=Masters+Fencing+St+Marys+GA' },
                { icon: 'fa-clock',        text: 'Mon–Sat: 6:00 am – 6:00 pm',               href: null },
              ].map((item) => (
                <div key={item.text} className="flex items-center gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#C4A84F] shrink-0">
                    <i className={`fa-solid ${item.icon} text-sm text-[#0D0C0B]`} />
                  </div>
                  {item.href ? (
                    <a
                      href={item.href}
                      target={item.href.startsWith('http') ? '_blank' : undefined}
                      rel={item.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                      className="text-[#F5F1E8]/75 text-sm font-sans hover:text-[#C4A84F] transition-colors leading-snug hover-underline"
                    >
                      {item.text}
                    </a>
                  ) : (
                    <span className="text-[#F5F1E8]/75 text-sm font-sans">{item.text}</span>
                  )}
                </div>
              ))}
            </div>

            {/* Urgency callout */}
            <div className="border border-[#C4A84F]/45 bg-[#C4A84F]/10 p-6 mb-10">
              <div className="flex items-start gap-3">
                <i className="fa-solid fa-bolt text-[#C4A84F] text-sm mt-0.5 shrink-0" />
                <div>
                  <strong className="text-[#F5F1E8] text-sm font-sans font-semibold block mb-1">
                    Limited Offer: Save $100 On Your Project
                  </strong>
                  <p className="text-[#F5F1E8]/80 text-xs font-sans leading-relaxed">
                    Sign your estimate on the day of your free consultation and save $100. Military discount also available — ask when you call.
                  </p>
                </div>
              </div>
            </div>

            {/* Trust badges */}
            <div className="flex flex-wrap gap-2.5">
              {['Free Estimates', '5-Star Rated', 'Licensed & Insured', 'Military Discount'].map((badge) => (
                <span key={badge} className="badge-gold">{badge}</span>
              ))}
            </div>
          </div>

          {/* Right — form */}
          <div className="bg-[#FAF8F5] reveal-right">
            <div className="bg-[#0D0C0B] px-8 py-6 border-b-2 border-[#C4A84F]">
              <h3 className="font-serif text-[1.8rem] text-[#F5F1E8] mb-1">
                Request Your Free Estimate
              </h3>
              <p className="text-[#F5F1E8]/70 text-sm font-sans">
                Describe your project — we respond the same business day.
              </p>
            </div>

            <div className="p-8 md:p-10">
              {submitted ? (
                <div className="text-center py-14">
                  <div className="w-16 h-16 flex items-center justify-center bg-[#C4A84F] mx-auto mb-5">
                    <i className="fa-solid fa-check text-2xl text-[#0D0C0B]" />
                  </div>
                  <h4 className="font-serif text-[1.8rem] text-[#0D0C0B] mb-3">We'll Be In Touch Shortly!</h4>
                  <p className="text-[#0D0C0B]/70 text-sm font-sans leading-relaxed max-w-xs mx-auto">
                    Thank you for reaching out to Masters Fencing. Expect a call or message very soon to schedule your free on-site estimate.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-readdy-form className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="fname" className="block text-[10px] tracking-[0.22em] uppercase text-[#0D0C0B]/65 font-sans mb-2">
                        Full Name *
                      </label>
                      <input
                        id="fname" name="name" type="text" required
                        placeholder="Your name"
                        className="w-full border border-[#0D0C0B]/18 bg-white px-4 py-3.5 text-sm font-sans text-[#0D0C0B] focus:outline-none focus:border-[#C4A84F] transition-colors placeholder:text-[#0D0C0B]/38"
                      />
                    </div>
                    <div>
                      <label htmlFor="femail" className="block text-[10px] tracking-[0.22em] uppercase text-[#0D0C0B]/65 font-sans mb-2">
                        Email Address *
                      </label>
                      <input
                        id="femail" name="email" type="email" required
                        placeholder="your@email.com"
                        className="w-full border border-[#0D0C0B]/18 bg-white px-4 py-3.5 text-sm font-sans text-[#0D0C0B] focus:outline-none focus:border-[#C4A84F] transition-colors placeholder:text-[#0D0C0B]/38"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="fphone" className="block text-[10px] tracking-[0.22em] uppercase text-[#0D0C0B]/65 font-sans mb-2">
                      Phone Number *
                    </label>
                    <input
                      id="fphone" name="phone" type="tel" required
                      placeholder="(912) 000-0000"
                      className="w-full border border-[#0D0C0B]/18 bg-white px-4 py-3.5 text-sm font-sans text-[#0D0C0B] focus:outline-none focus:border-[#C4A84F] transition-colors placeholder:text-[#0D0C0B]/38"
                    />
                  </div>

                  <div>
                    <label htmlFor="fservice" className="block text-[10px] tracking-[0.22em] uppercase text-[#0D0C0B]/65 font-sans mb-2">
                      Fencing Service Needed
                    </label>
                    <select
                      id="fservice" name="service"
                      className="w-full border border-[#0D0C0B]/18 bg-white px-4 py-3.5 text-sm font-sans text-[#0D0C0B] focus:outline-none focus:border-[#C4A84F] transition-colors cursor-pointer"
                    >
                      <option value="">Select a service</option>
                      <option value="Chain-Link Fencing">Chain-Link Fencing</option>
                      <option value="Wood Privacy Fence">Wood Privacy Fence</option>
                      <option value="Steel Fence">Steel Fence</option>
                      <option value="Vinyl Fence">Vinyl Fence</option>
                      <option value="Aluminum Fence">Aluminum Fence</option>
                      <option value="Custom Gate">Custom Gate</option>
                      <option value="Not Sure Yet">Not Sure Yet</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="fmessage" className="block text-[10px] tracking-[0.22em] uppercase text-[#0D0C0B]/65 font-sans mb-2">
                      Project Details
                    </label>
                    <textarea
                      id="fmessage" name="message" rows={4} maxLength={500}
                      placeholder="Approximate footage, property type, timeline, or any special requirements..."
                      className="w-full border border-[#0D0C0B]/18 bg-white px-4 py-3.5 text-sm font-sans text-[#0D0C0B] focus:outline-none focus:border-[#C4A84F] transition-colors resize-none placeholder:text-[#0D0C0B]/38"
                    />
                  </div>

                  {error && (
                    <p className="text-red-600 text-xs font-sans">
                      Something went wrong. Please try again or call us at 912-409-2031.
                    </p>
                  )}

                  <button
                    type="submit" disabled={loading}
                    className="w-full bg-[#C4A84F] text-[#0D0C0B] text-[11px] font-bold tracking-[0.22em] uppercase py-5 hover:bg-[#D4BC78] transition-all duration-300 disabled:opacity-70 whitespace-nowrap cursor-pointer"
                  >
                    {loading ? 'Sending Your Request...' : 'Get My Free Estimate →'}
                  </button>

                  <p className="text-center text-[10px] text-[#0D0C0B]/55 font-sans">
                    Or call directly:{' '}
                    <a href="tel:912-409-2031" className="text-[#C4A84F] hover:underline font-medium">
                      912-409-2031
                    </a>
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FinalCTA;
